import React, {useEffect} from 'react';
import DialogComponent from 'components/Dialog';
import {DialogTypes} from 'common/names';
import {EmailNotificationDefinition} from 'types/EmailNotificationDefinition';
import FormikTextField from 'components/inputs/FormikTextField';
import FormikDropdown from 'components/inputs/FormikDropdown';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import Panel from 'components/Panel';
import LastUpdatedLabel from 'components/LastUpdatedLabel';
import {IITSupportEmailSettings, ITSupportEmailSettings} from 'types/ITSupportEmailSettings';
import {Grid, ListItem, ListItemSecondaryAction} from '@mui/material';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import {NotificationType} from 'types/diplomat/enums/NotificationType';
import {useTranslation} from 'react-i18next';
import {useGetApi} from 'common/hooks/useGetApi';
import api from 'api';
import {usePostApi} from 'common/hooks/usePostApi';
import Loader from 'components/Loader';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import Button from 'components/Button';
import IconButton from '@mui/material/IconButton';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Formik from 'components/hook-form/Formik';
import {useFieldArray} from 'react-hook-form';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';
import {uniqueId} from 'lodash';

type ITSupportEmailSettingsDialogProps = {
   onClose: () => void;
};

const useStyles = makeStyles((theme) => ({
   centered: {
      alignSelf: 'center',
   },
   listItem: {
      paddingLeft: 0,
   },
   addButton: {
      textTransform: 'none',
   },
}));

const ITSupportEmailSettingsForm = ({onClose}: ITSupportEmailSettingsDialogProps) => {
   const classes = useStyles();
   const {t} = useTranslation();

   const {fields, insert, remove} = useFieldArray<IITSupportEmailSettings>({
      name: 'notifications.definitions',
   });
   const getValue = useFieldValueGetter<IITSupportEmailSettings>();

   const timestamp = getValue('timestamp');
   const lastUser = getValue('lastUser');

   return (
      <>
         <Panel name={t('IT Support - Email Address / Notification Type')}>
            <List dense>
               {fields.map((definition, i) => (
                  <ListItem
                     key={
                        definition._id ||
                        definition.address + definition.type ||
                        uniqueId('definition')
                     }
                     className={classes.listItem}
                  >
                     <Grid container spacing={1} wrap={'nowrap'}>
                        <Grid item>
                           <FormikTextField
                              autoFocus={i === 0}
                              type="email"
                              name={`notifications.definitions[${i}].address`}
                           />
                        </Grid>
                        <Grid item xs={3}>
                           <FormikDropdown
                              name={`notifications.definitions[${i}].type`}
                              options={NotificationType.options}
                           />
                        </Grid>
                        <Grid item xs className={classes.centered}>
                           <Typography noWrap variant={'body2'}>
                              {t('Send Status Email on:')}
                           </Typography>
                        </Grid>
                        <Grid item className={classes.centered}>
                           <FormikCheckbox
                              name={`notifications.definitions[${i}].serverStartupEmails`}
                              label={t('Service Restart')}
                           />
                        </Grid>
                        <Grid item className={classes.centered}>
                           <FormikCheckbox
                              name={`notifications.definitions[${i}].keyExpirationEmails`}
                              label={t('Key Expiration')}
                           />
                        </Grid>
                     </Grid>
                     <ListItemSecondaryAction>
                        {fields.length > 1 && (
                           <IconButton size={'small'} onClick={() => remove(i)}>
                              <ClearRoundedIcon color={'secondary'} />
                           </IconButton>
                        )}
                     </ListItemSecondaryAction>
                  </ListItem>
               ))}
               <ListItem className={classes.listItem}>
                  <Button
                     className={classes.addButton}
                     variant={'text'}
                     startIcon={<AddRoundedIcon color={'primary'} />}
                     onClick={() =>
                        insert(
                           fields.length,
                           EmailNotificationDefinition.cast({
                              _id: uniqueId('email-notify'),
                           })
                        )
                     }
                  >
                     {t('Add Notification')}
                  </Button>
               </ListItem>
            </List>
         </Panel>
         <LastUpdatedLabel date={timestamp} author={lastUser} />
         <FormFooterButtons cancel={onClose} submitLabel={t('Save')} />
      </>
   );
};

const ITSupportEmailSettingsDialog = ({onClose}: ITSupportEmailSettingsDialogProps) => {
   const {
      request: fetchITSupportSettings,
      state: settings,
      loading,
   } = useGetApi<IITSupportEmailSettings>(api.supportEmailSettings);
   const {request: saveITSupportSettings} = usePostApi(api.supportEmailSettings, {
      onSuccess: onClose,
   });

   useEffect(() => {
      fetchITSupportSettings();
   }, [fetchITSupportSettings]);

   const saveSettings = (values: IITSupportEmailSettings) => {
      saveITSupportSettings(JSON.stringify(values));
   };

   return (
      <DialogComponent
         maxWidth="md"
         header={DialogTypes.ITSupportEmailSettingsDialog.dialogName}
         close={onClose}
      >
         {loading || !settings ? (
            <Loader />
         ) : (
            <Formik
               initialValues={ITSupportEmailSettings.cast(settings) as IITSupportEmailSettings}
               validationSchema={ITSupportEmailSettings}
               onSubmit={saveSettings}
               enableReinitialize
            >
               <ITSupportEmailSettingsForm onClose={onClose} />
            </Formik>
         )}
      </DialogComponent>
   );
};

export default ITSupportEmailSettingsDialog;
